import React from "react";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Legend, Filler);

interface ReasonEvictionData {
  total_evictions_individuals: number;
  total_evictions_households: number;
}

interface EvictionsByTimePeriod {
  [timePeriod: string]: {
    [reason: string]: ReasonEvictionData;
  };
}

interface EvictionsReasonLineChartProps {
  data?: EvictionsByTimePeriod | null;
  title?: string;
  timePeriodLabel?: string; // e.g., "Year" or "Month"
}

const colorPalette = [
  "#D32F2F", // Strong Red
  "#1976D2", // Bold Blue
  "#388E3C", // Dark Green
  "#FBC02D", // Bright Yellow
  "#8E24AA", // Deep Purple
  "#0288D1", // Light Blue
  "#7B1FA2", // Purple
  "#FF9800", // Orange
  "#009688", // Teal
  "#C2185B", // Pink
  "#0288D1", // Sky Blue
  "#8E44AD", // Violet
];

const monthOrder = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

const EvictionsReasonLineChart: React.FC<EvictionsReasonLineChartProps> = ({
  data,
  title = "Evictions by Reason (Individuals) Over Time",
  timePeriodLabel = "Time Period",
}) => {
  const windowSize = 9;
  const hasInitialized = React.useRef(false);

  const [allPeriods, setAllPeriods] = React.useState<string[]>([]);
  const [startIndex, setStartIndex] = React.useState(0);
  const [chartData, setChartData] = React.useState<any>(null);

  const hasData = data && Object.keys(data).length > 0;

  // Effect 1: compute allPeriods
  React.useEffect(() => {
    if (!hasData) return;

    let periods = Object.keys(data!);

    if (periods.every(p => monthOrder.includes(p))) {
      periods.sort((a, b) => monthOrder.indexOf(a) - monthOrder.indexOf(b));
    } else {
      periods.sort((a, b) => a.localeCompare(b));
    }

    const currentYear = new Date().getFullYear();
    periods = periods.filter(p => isNaN(Number(p)) || Number(p) <= currentYear);

    setAllPeriods(periods);
  }, [data, hasData]);

  // Effect 2: initialize window
  React.useEffect(() => {
    if (allPeriods.length > 0 && !hasInitialized.current) {
      const lastIndex = Math.max(0, allPeriods.length - windowSize);
      setStartIndex(lastIndex);
      hasInitialized.current = true;
    }
  }, [allPeriods]);

  // Effect 3: update chartData
  React.useEffect(() => {
    if (!hasData || allPeriods.length === 0) return;

    const visiblePeriods = allPeriods.slice(startIndex, startIndex + windowSize);
    const firstPeriod = visiblePeriods[0];
    const reasons = firstPeriod && data![firstPeriod] ? Object.keys(data![firstPeriod]) : [];

    const datasets = reasons.map((reason, idx) => {
      const dataPoints = visiblePeriods.map(
        period => data![period]?.[reason]?.total_evictions_individuals || 0
      );
      return {
        label: reason,
        data: dataPoints,
        borderColor: colorPalette[idx % colorPalette.length],
        backgroundColor: colorPalette[idx % colorPalette.length] + "33",
        borderWidth: 1,
        pointBackgroundColor: "#fff",
        pointBorderColor: colorPalette[idx % colorPalette.length],
        pointBorderWidth: 1,
        pointRadius: 3,
        pointHoverRadius: 5,
        fill: false,
        tension: 0.1,
      };
    });

    setChartData({
      labels: visiblePeriods,
      datasets,
    });
  }, [startIndex, allPeriods, data, hasData]);

  const options: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: false,
        text: title,
        font: { size: 12, weight: "bold" },
        color: "#333",
        padding: { top: 0, bottom: 10 },
      },
      legend: {
        display: true,
        position: "top",
        labels: {
          boxWidth: 12,
          boxHeight: 12,
          padding: 10,
          font: { size: 10 },
          usePointStyle: true,
        },
      },
      tooltip: {
        backgroundColor: "#fff",
        borderColor: "#ddd",
        borderWidth: 1,
        titleColor: "#333",
        bodyColor: "#333",
        padding: 10,
        callbacks: {
          label: (context) => `${context.dataset.label}: ${context.raw?.toLocaleString()}`,
          title: (context) => `${timePeriodLabel}: ${context[0].label}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 10 }, color: "#666" },
      },
      y: {
        beginAtZero: true,
        grid: { color: "rgba(180, 180, 180, 0.1)" },
        ticks: {
          font: { size: 10 },
          color: "#666",
          callback: function (value) {
            if (typeof value === "number") {
              if (value >= 1_000_000) return (value / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
              if (value >= 1_000) return (value / 1_000).toFixed(0) + "K";
              return value.toString();
            }
            return value;
          },
        },
      },
    },
  };

  return (
    <div className="p-2 bg-white rounded-md relative">
      {!hasData || !chartData ? (
        <div className="flex items-center justify-center h-64 gap-2 text-sm text-gray-500">
          <svg
            className="w-5 h-5 animate-spin text-blue-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          Loading chart data...
        </div>
      ) : (
        <>
          <div style={{ minHeight: "350px", width: "100%" }}>
            <Line data={chartData} options={options} />
          </div>

          {/* Pagination buttons */}
          <div className="absolute inset-x-0 -bottom-4 flex justify-between px-2">
            <button
              className="w-6 h-6 flex items-center justify-center rounded-md bg-[#E5E7EB] hover:bg-[#D1D5DB] disabled:opacity-50"
              onClick={() => setStartIndex(Math.max(0, startIndex - 1))}
              disabled={startIndex === 0}
            >
              <div className="w-0 h-0 border-t-[6px] border-b-[6px] border-r-[6px] border-transparent border-r-[#374151]" />
            </button>
            <button
              className="w-6 h-6 flex items-center justify-center rounded-md bg-[#E5E7EB] hover:bg-[#D1D5DB] disabled:opacity-50"
              onClick={() => setStartIndex(Math.min(startIndex + 1, allPeriods.length - windowSize))}
              disabled={startIndex >= allPeriods.length - windowSize}
            >
              <div className="w-0 h-0 border-t-[6px] border-b-[6px] border-l-[6px] border-transparent border-l-[#374151]" />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default EvictionsReasonLineChart;